import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ENCRYPTED_QUERY_PARAM,
  resolveEncryptedSearchParams,
} from "../utils/encryptedQuery";

/**
 * Decrypts `?q=ENC:...` (BCM list, checklist, etc.).
 * Falls back to plain query params for backward compatibility.
 *
 * Never expose decrypted params from a *previous* `q=` while a new one is resolving —
 * that caused first dashboard click to bind wrong/empty filters (refresh then worked).
 */
export function useEncryptedSearchParams() {
  const [rawParams] = useSearchParams();
  const encryptedQ = rawParams.get(ENCRYPTED_QUERY_PARAM);
  const hasEncrypted = Boolean(encryptedQ);
  const rawKey = rawParams.toString();
  const lastGoodRef = useRef(null);
  const lastGoodRawKeyRef = useRef(null);

  const [searchParams, setSearchParams] = useState(() => {
    if (hasEncrypted) return null;
    const plain = new URLSearchParams(rawParams);
    lastGoodRef.current = plain;
    lastGoodRawKeyRef.current = rawKey;
    return plain;
  });
  const [resolvedRawKey, setResolvedRawKey] = useState(() =>
    hasEncrypted ? null : rawKey
  );

  useEffect(() => {
    let cancelled = false;
    const snapshot = new URLSearchParams(rawKey);

    if (!hasEncrypted) {
      lastGoodRef.current = snapshot;
      lastGoodRawKeyRef.current = rawKey;
      setSearchParams(snapshot);
      setResolvedRawKey(rawKey);
      return () => {
        cancelled = true;
      };
    }

    // New encrypted URL — drop stale decrypted params until this key resolves.
    if (lastGoodRawKeyRef.current !== rawKey) {
      setSearchParams(null);
      setResolvedRawKey(null);
    }

    resolveEncryptedSearchParams(snapshot)
      .then((resolved) => {
        if (cancelled) return;
        lastGoodRef.current = resolved;
        lastGoodRawKeyRef.current = rawKey;
        setSearchParams(resolved);
        setResolvedRawKey(rawKey);
      })
      .catch(() => {
        if (cancelled) return;
        if (lastGoodRawKeyRef.current === rawKey && lastGoodRef.current) {
          setSearchParams(lastGoodRef.current);
          setResolvedRawKey(rawKey);
        } else {
          setSearchParams(new URLSearchParams());
          setResolvedRawKey(rawKey);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [rawKey, hasEncrypted]);

  const paramsReady =
    !hasEncrypted ||
    (searchParams != null && resolvedRawKey === rawKey);

  return {
    searchParams: paramsReady ? searchParams : null,
    paramsReady,
    rawSearchParams: rawParams,
    encryptedQuery: encryptedQ,
  };
}
